// RITA-1525 (Teilpaket 2) — Bearbeitbare Seitenansicht.
//
// Der Seitencontainer ist der einzige contentEditable-Host; das DOM wird nie direkt verändert:
// `beforeinput` wird abgefangen, die Eingabe als Modelloperation ausgeführt, React rendert die
// Seiten neu, danach wird die DOM-Auswahl aus der Modellauswahl wiederhergestellt. Native Listener
// sind nötig, weil React 17 `beforeinput` nicht als natives Ereignis liefert. Vor jeder Eingabe wird
// die DOM-Auswahl synchron ins Modell übernommen (`selectionchange` kommt asynchron und in jsdom gar
// nicht). Ausnahme ist die Komposition (IME, Dead Keys wie ´+e): Sie darf das DOM vorübergehend
// ändern; bei `compositionend` wird der Text ins Modell übernommen und alle Seiten werden neu
// aufgebaut, damit keine nativen DOM-Änderungen zurückbleiben.
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import VertragSeiten from '../components/VertragSeiten';
import { Seitengeometrie } from '../layout/seitengeometrie';
import { Seiteninhalt } from '../layout/seitenAufteilen';
import { textDerAuswahl } from '../model/bearbeiten';
import { Wertemodus } from '../model/platzhalterwerte';
import {
  Auswahl,
  auswahlenGleich,
  auswahlEntHaengen,
  auswahlSortiert,
  Bereich,
  istKollabiert,
  kollabiert,
  Position,
  positionenGleich,
  vergleichePositionen,
} from '../model/positionen';
import {
  auswahlAusDom,
  auswahlAusDomGeklemmt,
  auswahlInDom,
  domZuPosition,
  inselGrenzen,
  positionAmPunkt,
  rangeZuAuswahl,
  zielAmPunkt,
} from './domAuswahl';
import { VertragEditorApi } from './useVertragEditor';

interface Props {
  editor: VertragEditorApi;
  seiten: Seiteninhalt[];
  modus: Wertemodus;
  geometrie: Seitengeometrie;
  gesperrt?: boolean;
  scrollRandOben?: () => number;
}

const BEREICHE: readonly string[] = ['text', 'kopf', 'fuss'];
/** Ab dieser Mausbewegung (px) wird aus einem Klick auf einen Platzhalter eine Markierung (RITA-1533, V10). */
const ZIEH_SCHWELLE_PX = 4;
/** Randzone (px) am oberen (unter den festen Leisten) und unteren Fensterrand, in der eine geführte Markierung mitscrollt. */
const ZIEH_SCROLL_RAND_PX = 24;
/** Scrollweg (px) je Bildschirmaufbau beim Mitscrollen einer geführten Markierung. */
const ZIEH_SCROLL_SCHRITT_PX = 12;

/** Eine auf einem Platzhalter begonnene Mausauswahl, die der Editor selbst führt (RITA-1533, V10). */
interface Ziehstart {
  insel: Element;
  grenzen: { davor: Position; dahinter: Position };
  bereich: Bereich;
  x: number;
  y: number;
  /** true, sobald die Maus die Schwelle überschritten und den Platzhalter verlassen hat (dann ist es eine Markierung). */
  begonnen: boolean;
}
/**
 * Abstand zum festen App-Kopf (AppBar 92 px plus 4 px Luft): fürs Scrollen zur Schreibmarke und für klebende Leisten
 * neben den Seiten (Platzhalterleiste). Bewusst nicht aus `config-global` importiert – das zöge einen
 * Importzyklus über `pages` nach sich.
 */
export const APP_KOPF_ABSTAND_PX = 96;
// Beim Scrollen zur Schreibmarke: oben derselbe Abstand wie zum App-Kopf, unten reicht ein kleiner Rand.
const SCROLL_RAND_OBEN_PX = APP_KOPF_ABSTAND_PX;
const SCROLL_RAND_UNTEN_PX = 24;

function zielAuswahl(event: InputEvent): Auswahl | null {
  const ranges = typeof event.getTargetRanges === 'function' ? event.getTargetRanges() : [];
  return ranges.length > 0 ? rangeZuAuswahl(ranges[0]) : null;
}

/** Scrollt nur das Fenster, damit der Caret sichtbar bleibt (nie die Seitenkästen mit overflow: hidden). */
function caretSichtbarMachen(dokument: Document, scrollRandOben?: () => number, aktiveKante = false): void {
  const auswahl = dokument.getSelection();
  if (!auswahl || auswahl.rangeCount === 0) return;
  const range = auswahl.getRangeAt(0).cloneRange();
  if (aktiveKante && auswahl.focusNode) {
    range.setStart(auswahl.focusNode, auswahl.focusOffset);
    range.collapse(true);
  } else range.collapse(false);
  if (typeof range.getBoundingClientRect !== 'function') return;
  let rect = range.getBoundingClientRect();
  if (rect.height === 0) {
    const knoten = range.startContainer;
    const element =
      knoten.nodeType === Node.ELEMENT_NODE
        ? ((knoten as Element).children[range.startOffset] ?? (knoten as Element))
        : knoten.parentElement;
    if (!element) return;
    rect = element.getBoundingClientRect();
    // Eine leere Zeile hat in Chromium teils weder eine Range-Hoehe noch ein messbares BR.
    // Dann ist der umgebende Absatz/Listeneintrag die sichtbare Schreibzeile, nicht die ganze Seite.
    if (rect.height === 0) {
      const zeile = element.closest('li[data-eintrag], [data-block-id]');
      if (zeile) rect = zeile.getBoundingClientRect();
    }
  }
  if (rect.height === 0 && rect.width === 0) return;
  const fenster = dokument.defaultView;
  if (!fenster) return;
  const randOben = scrollRandOben?.() ?? SCROLL_RAND_OBEN_PX;
  if (rect.top < randOben) fenster.scrollBy({ top: rect.top - randOben });
  else if (rect.bottom > fenster.innerHeight - SCROLL_RAND_UNTEN_PX) {
    fenster.scrollBy({ top: rect.bottom - fenster.innerHeight + SCROLL_RAND_UNTEN_PX });
  }
}

function VertragEditor({ editor, seiten, modus, geometrie, gesperrt = false, scrollRandOben }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const { zustand, dispatch, befehle } = editor;
  const aktiveSeite = Math.min(Math.max(1, zustand.aktiveSeite), Math.max(1, seiten.length));
  const zustandRef = useRef(zustand);
  zustandRef.current = zustand;
  const befehleRef = useRef(befehle);
  befehleRef.current = befehle;
  const gesperrtRef = useRef(gesperrt);
  gesperrtRef.current = gesperrt;
  const aktiveSeiteRef = useRef(aktiveSeite);
  aktiveSeiteRef.current = aktiveSeite;
  /** undefined = keine Komposition; sonst die ursprüngliche Auswahl beim Kompositionsstart. */
  const kompositionRef = useRef<Auswahl | null | undefined>(undefined);
  const mausGedruecktRef = useRef(false);
  const ziehstartRef = useRef<Ziehstart | null>(null);
  const fokusBearbeitetRef = useRef(0);
  const bearbeitetRef = useRef(false);
  const scrollRandObenRef = useRef(scrollRandOben);
  scrollRandObenRef.current = scrollRandOben;
  const letzterScrollRandRef = useRef<number>();
  const scrollNachLayoutRef = useRef<number>();
  const scrollenNachLayout = useCallback(() => {
    if (scrollNachLayoutRef.current !== undefined) window.cancelAnimationFrame(scrollNachLayoutRef.current);
    const erwartet = zustandRef.current.auswahl;
    scrollNachLayoutRef.current = window.requestAnimationFrame(() => {
      scrollNachLayoutRef.current = undefined;
      const host = hostRef.current;
      const aktuell = zustandRef.current.auswahl;
      if (
        !gesperrtRef.current &&
        kompositionRef.current === undefined &&
        !mausGedruecktRef.current &&
        host &&
        document.activeElement === host &&
        erwartet &&
        aktuell &&
        auswahlenGleich(erwartet, aktuell)
      )
        caretSichtbarMachen(document, scrollRandObenRef.current);
    });
  }, []);
  useEffect(
    () => () => {
      if (scrollNachLayoutRef.current !== undefined) window.cancelAnimationFrame(scrollNachLayoutRef.current);
    },
    [],
  );
  /** Zuletzt gelesener DOM-Punkt samt Ergebnis: derselbe Punkt ergibt dieselbe Position (kein Pendeln). */
  const letzterDomPunktRef = useRef<{
    punkt: string;
    auswahl: Auswahl;
    anchorNode: Node | null;
    focusNode: Node | null;
  } | null>(null);

  /** Übernimmt die DOM-Auswahl synchron ins Modell; korrigiert sie im DOM, wenn sie in einer Insel/Lücke liegt. */
  const auswahlUebernehmen = useCallback(
    (korrigierenErlaubt: boolean): Auswahl | null => {
      const host = hostRef.current;
      if (!host) return null;
      const aktuell = zustandRef.current;
      if (gesperrtRef.current) return aktuell.auswahl;
      const dom = host.ownerDocument.getSelection();
      const punkt = dom
        ? [dom.anchorNode?.nodeName, dom.anchorOffset, dom.focusNode?.nodeName, dom.focusOffset, aktuell.bereich].join(
            '|',
          )
        : '';
      const gleicherPunkt =
        dom &&
        letzterDomPunktRef.current &&
        letzterDomPunktRef.current.punkt === punkt &&
        dom.anchorNode === letzterDomPunktRef.current.anchorNode &&
        dom.focusNode === letzterDomPunktRef.current.focusNode;
      const gelesen = gleicherPunkt
        ? { auswahl: letzterDomPunktRef.current!.auswahl, korrigieren: true }
        : auswahlAusDomGeklemmt(host, aktuell.bereich, aktuell.auswahl);
      if (!gelesen) return aktuell.auswahl;
      if (dom && !gleicherPunkt) {
        letzterDomPunktRef.current = {
          punkt,
          auswahl: gelesen.auswahl,
          anchorNode: dom.anchorNode,
          focusNode: dom.focusNode,
        };
      }
      if (!auswahlenGleich(gelesen.auswahl, aktuell.auswahl)) dispatch({ typ: 'auswahl', auswahl: gelesen.auswahl });
      if (gelesen.korrigieren && korrigierenErlaubt) auswahlInDom(host, gelesen.auswahl, aktiveSeiteRef.current);
      return gelesen.auswahl;
    },
    [dispatch],
  );

  // Die Werkzeugleiste liest die Auswahl vor jedem Befehl über den Editor.
  useEffect(() => {
    const leser = editor.auswahlLeser;
    leser.current = () => {
      if (kompositionRef.current === undefined) auswahlUebernehmen(false);
    };
    return () => {
      leser.current = null;
    };
  }, [editor.auswahlLeser, auswahlUebernehmen]);

  // DOM-Auswahl → Modell (Werkzeugleiste, „Seite X von Y“); während Maus-Ziehen ohne DOM-Korrektur.
  useEffect(() => {
    const dokument = document;
    const onSelectionChange = () => {
      if (gesperrtRef.current) return;
      if (kompositionRef.current !== undefined) return;
      const auswahl = dokument.getSelection();
      const host = hostRef.current;
      if (!host || !auswahl || !auswahl.anchorNode) return;
      // Sicherung während einer vom Editor geführten Markierung ab einem Platzhalter: Verändert der Browser
      // die Auswahl dabei selbst, gilt die Modellauswahl und wird ins DOM zurückgeschrieben. (In den
      // Chromium-Messungen vom 08.10.2026 kam das nicht vor; das native Ziehen ist durch das verhinderte
      // mousedown aus.) Nur ab der ersten Bewegung über die Schwelle, nicht beim bloßen Klick.
      if (ziehstartRef.current?.begonnen && zustandRef.current.auswahl) {
        auswahlInDom(host, zustandRef.current.auswahl, aktiveSeiteRef.current);
        return;
      }
      if (!host.contains(auswahl.anchorNode)) return;
      auswahlUebernehmen(!mausGedruecktRef.current);
    };
    const onMouseDown = () => {
      mausGedruecktRef.current = true;
    };
    // RITA-1533 (V10): Eine auf einem Platzhalter begonnene Markierung führt der Editor selbst, weil
    // Chromium eine Auswahl mit Anker in einer nicht editierbaren Insel in diese Insel einsperrt
    // (gemessen 08.10.2026). Das native Ziehen ist durch preventDefault im mousedown unterbunden; damit
    // entfällt auch Chromiums Mitscrollen am Fensterrand (gemessen 08.10.2026) – das übernimmt `mitscrollen`.
    const ziehenFolgen = (host: HTMLElement, start: Ziehstart, x: number, y: number) => {
      const aktuell = zustandRef.current;
      // Außerhalb des Hosts (Leiste, Werkzeugleiste, App-Kopf) oder in der Seitenlücke: die zuletzt
      // gültige Markierung bleibt (sonst klemmte die DOM-Reihenfolge auf Textanfang oder -ende).
      const treffer = zielAmPunkt(host, x, y, start.bereich, aktuell.auswahl?.fokus ?? null);
      if (!treffer) return;
      const vorwaerts = vergleichePositionen(aktuell.dokument, treffer.position, start.grenzen.davor) > 0;
      const anker = vorwaerts ? start.grenzen.davor : start.grenzen.dahinter;
      let fokus = treffer.position;
      if (treffer.insel) fokus = vorwaerts ? treffer.insel.dahinter : treffer.insel.davor;
      if (positionenGleich(fokus, anker)) fokus = vorwaerts ? start.grenzen.dahinter : start.grenzen.davor;
      const auswahl: Auswahl = { anker, fokus };
      if (aktuell.auswahl && auswahlenGleich(auswahl, aktuell.auswahl)) return;
      dispatch({ typ: 'auswahl', auswahl });
      auswahlInDom(host, auswahl, aktiveSeiteRef.current);
    };
    let zeiger = { x: 0, y: 0 };
    let scrollRahmen: number | undefined;
    const scrollSchritt = (y: number): number => {
      const randOben = scrollRandObenRef.current?.() ?? SCROLL_RAND_OBEN_PX;
      if (y < randOben + ZIEH_SCROLL_RAND_PX) return -ZIEH_SCROLL_SCHRITT_PX;
      if (y > window.innerHeight - ZIEH_SCROLL_RAND_PX) return ZIEH_SCROLL_SCHRITT_PX;
      return 0;
    };
    const mitscrollenStoppen = () => {
      if (scrollRahmen === undefined) return;
      window.cancelAnimationFrame(scrollRahmen);
      scrollRahmen = undefined;
    };
    const ziehenBeenden = () => {
      ziehstartRef.current = null;
      mitscrollenStoppen();
    };
    // Solange der Zeiger bei gedrückter Taste in der Randzone steht, scrollt das Fenster Bild für Bild weiter
    // und die Markierung folgt dem Inhalt, der unter den Zeiger rückt.
    const mitscrollen = () => {
      scrollRahmen = undefined;
      const start = ziehstartRef.current;
      const host = hostRef.current;
      const schritt = scrollSchritt(zeiger.y);
      if (!start || !start.begonnen || !host || schritt === 0) return;
      if (gesperrtRef.current || kompositionRef.current !== undefined) {
        ziehenBeenden();
        return;
      }
      window.scrollBy(0, schritt);
      ziehenFolgen(host, start, zeiger.x, zeiger.y);
      scrollRahmen = window.requestAnimationFrame(mitscrollen);
    };
    const onMouseMove = (event: MouseEvent) => {
      const start = ziehstartRef.current;
      const host = hostRef.current;
      if (!start) return;
      // Ohne gedrückte Primärtaste ging das mouseup verloren (Fensterwechsel, Systemdialog): Das Ziehen endet
      // hier, sonst liefe das Mitscrollen weiter und der nächste Klick würde zurückgenommen (Review-Befund B1).
      // Ebenso bei Sperre oder begonnener Komposition.
      // eslint-disable-next-line no-bitwise
      if ((event.buttons & 1) !== 1 || !host || gesperrtRef.current || kompositionRef.current !== undefined) {
        ziehenBeenden();
        return;
      }
      if (!start.begonnen) {
        if (Math.hypot(event.clientX - start.x, event.clientY - start.y) < ZIEH_SCHWELLE_PX) return;
        const ziel = event.target instanceof Element ? event.target : null;
        // Noch auf dem Start-Platzhalter: bleibt ein Klick.
        if (ziel && start.insel.contains(ziel)) return;
        start.begonnen = true;
      }
      // Ab hier folgt die Markierung jeder Bewegung – auch zurück auf den Start-Platzhalter (dann genau er).
      zeiger = { x: event.clientX, y: event.clientY };
      ziehenFolgen(host, start, zeiger.x, zeiger.y);
      if (scrollRahmen === undefined && scrollSchritt(zeiger.y) !== 0) {
        scrollRahmen = window.requestAnimationFrame(mitscrollen);
      }
    };
    const onMouseUp = () => {
      mausGedruecktRef.current = false;
      ziehenBeenden();
      // Nach dem Loslassen die während des Ziehens ausgesetzte Korrektur nachholen (Klick in eine
      // Insel oder in die Seitenlücke): sonst bliebe der Caret dort stehen, wo Chromium keine Eingabe annimmt.
      if (kompositionRef.current === undefined) auswahlUebernehmen(true);
    };
    dokument.addEventListener('selectionchange', onSelectionChange);
    dokument.addEventListener('mousedown', onMouseDown, true);
    dokument.addEventListener('mousemove', onMouseMove);
    dokument.addEventListener('mouseup', onMouseUp, true);
    // Verlässt der Fokus das Fenster mitten im Ziehen, kommt kein mouseup mehr: ebenfalls beenden.
    window.addEventListener('blur', ziehenBeenden);
    return () => {
      ziehenBeenden();
      dokument.removeEventListener('selectionchange', onSelectionChange);
      dokument.removeEventListener('mousedown', onMouseDown, true);
      dokument.removeEventListener('mousemove', onMouseMove);
      dokument.removeEventListener('mouseup', onMouseUp, true);
      window.removeEventListener('blur', ziehenBeenden);
    };
  }, [auswahlUebernehmen]);

  // Chromium scrollt bei Navigationstasten selbst, kennt aber unsere klebenden Leisten nicht.
  // Nach seiner Auswahlbewegung die aktive Kante nachfuehren, auch rueckwaerts mit Shift; keine Mausmarkierungen.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let frame: number | undefined;
    const nachfuehren = (event: KeyboardEvent) => {
      if (!/^(ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Home|End|PageUp|PageDown)$/.test(event.key)) return;
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const auswahl = document.getSelection();
        if (
          !gesperrtRef.current &&
          scrollRandObenRef.current &&
          document.activeElement === host &&
          kompositionRef.current === undefined &&
          auswahl?.focusNode &&
          host.contains(auswahl.focusNode)
        )
          caretSichtbarMachen(document, scrollRandObenRef.current, true);
      });
    };
    host.addEventListener('keydown', nachfuehren);
    host.addEventListener('keyup', nachfuehren);
    return () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      host.removeEventListener('keydown', nachfuehren);
      host.removeEventListener('keyup', nachfuehren);
    };
  }, []);

  // Eingabeereignisse am Host: alles läuft über das Modell.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    // Einfügen aus der Zwischenablage (paste und Rückfall insertFromPaste). Während einer offenen Komposition
    // (´ getippt, Buchstabe fehlt noch) wird die DOM-Auswahl nicht übernommen, sie steht im vorläufigen
    // Browsertext. Der Reducer verwirft dann die Komposition und fügt an der ursprünglichen Markierung
    // ein – wie bei einem Platzhalter-Klick; ein spätes compositionend baut nur noch neu auf.
    // Die Abfrage unten ist eine zweite Sicherung: Der Reducer übernimmt während einer Komposition ohnehin
    // keine Auswahl. Sie lässt sich deshalb nicht über sichtbares Verhalten testen und folgt der Regel
    // „währenddessen keine DOM-Auswahl aus dem vorläufigen Browsertext übernehmen“ (docs/HOTSPOTS.md).
    const zwischenablageEinfuegen = (text: string) => {
      if (gesperrtRef.current) return;
      if (kompositionRef.current === undefined) auswahlUebernehmen(false);
      if (!text) return;
      bearbeitetRef.current = true;
      befehleRef.current.textAusZwischenablageEinfuegen(text);
    };

    const onBeforeInput = (event: InputEvent) => {
      if (gesperrtRef.current) {
        event.preventDefault();
        return;
      }
      const typ = event.inputType;
      if (typ === 'insertCompositionText' || typ === 'deleteCompositionText') return;
      if (kompositionRef.current !== undefined) {
        // Nur das Einfügen wird auch jetzt abgefangen: Sonst schriebe der Browser den Text selbst ins DOM,
        // und das Ende der Komposition baute ihn aus dem Modell wieder weg (RITA-1525 Teil 4c, V9).
        // Der Zustand kommt aus kompositionRef; event.isComposing ist bei insertFromPaste in Chromium false.
        if (typ === 'insertFromPaste') {
          event.preventDefault();
          zwischenablageEinfuegen(event.dataTransfer?.getData('text/plain') ?? '');
        }
        return;
      }
      event.preventDefault();
      auswahlUebernehmen(false);
      const b = befehleRef.current;
      bearbeitetRef.current = true;
      switch (typ) {
        case 'insertText':
          b.textEinfuegen(event.data ?? '');
          break;
        case 'insertReplacementText': {
          const text = event.dataTransfer?.getData('text/plain') ?? event.data ?? '';
          b.textEinfuegen(text, zielAuswahl(event) ?? undefined, 'sonstiges');
          break;
        }
        case 'insertParagraph':
          b.absatzTeilen();
          break;
        case 'insertLineBreak':
          b.zeilenumbruch();
          break;
        case 'insertFromPaste': {
          // Rückfall: das paste-Ereignis unten ist der Hauptweg.
          const text = event.dataTransfer?.getData('text/plain') ?? '';
          if (text) b.textAusZwischenablageEinfuegen(text);
          break;
        }
        case 'deleteContentBackward':
          b.rueckwaerts();
          break;
        case 'deleteContentForward':
          b.vorwaerts();
          break;
        case 'deleteWordBackward':
        case 'deleteSoftLineBackward':
        case 'deleteHardLineBackward': {
          const ziel = zielAuswahl(event);
          if (ziel && !istKollabiert(ziel)) b.auswahlLoeschen(ziel);
          else b.rueckwaerts();
          break;
        }
        case 'deleteWordForward':
        case 'deleteSoftLineForward':
        case 'deleteHardLineForward': {
          const ziel = zielAuswahl(event);
          if (ziel && !istKollabiert(ziel)) b.auswahlLoeschen(ziel);
          else b.vorwaerts();
          break;
        }
        case 'deleteByCut':
          b.auswahlLoeschen();
          break;
        case 'historyUndo':
          b.rueckgaengig();
          break;
        case 'historyRedo':
          b.wiederholen();
          break;
        case 'formatBold':
          b.format('fett');
          break;
        case 'formatItalic':
          b.format('kursiv');
          break;
        case 'formatUnderline':
          b.format('unterstrichen');
          break;
        default:
          // insertFromDrop, deleteByDrag, insertOrderedList … sind in der Probeversion nicht vorgesehen.
          bearbeitetRef.current = false;
          break;
      }
    };

    // Die Auswahl wird nur vorläufig über das Modell gelöscht: Chromium darf Blöcke/React-Inseln
    // nicht selbst entfernen. Der Reducer merkt Dokument, Auswahl und den vollständigen Verlauf.
    const onCompositionStart = (event: CompositionEvent) => {
      if (gesperrtRef.current) {
        event.preventDefault();
        return;
      }
      const auswahl = auswahlUebernehmen(true);
      const dokumentVorher = zustandRef.current.dokument;
      kompositionRef.current = auswahl;
      befehleRef.current.kompositionStarten(auswahl);
      if (auswahl && !istKollabiert(auswahl)) {
        const entHaengt = auswahlEntHaengen(dokumentVorher, auswahl);
        const [start] = auswahlSortiert(dokumentVorher, entHaengt);
        auswahlInDom(host, kollabiert(start), aktiveSeiteRef.current);
      }
    };
    const onCompositionEnd = (event: CompositionEvent) => {
      // Nur eine vor der Sperre begonnene Eingabe darf noch abschließen. Eine währenddessen
      // verhinderte Komposition erzeugt weder Text noch einen unnötigen Seiten-Neuaufbau.
      if (gesperrtRef.current && kompositionRef.current === undefined) return;
      kompositionRef.current = undefined;
      bearbeitetRef.current = true;
      // Ohne laufende Transaktion wird nur neu aufgebaut: ein spätes Ereignis nach einem
      // Werkzeugleistenbefehl darf dessen Änderung weder überschreiben noch Text einfügen.
      befehleRef.current.kompositionAbschliessen(event.data ?? '');
    };

    const onCopy = (event: ClipboardEvent) => {
      if (kompositionRef.current !== undefined) return;
      const auswahl = gesperrtRef.current
        ? auswahlAusDomGeklemmt(host, zustandRef.current.bereich, zustandRef.current.auswahl)?.auswahl
        : auswahlUebernehmen(false);
      const { dokument } = zustandRef.current;
      if (!auswahl || istKollabiert(auswahl) || !event.clipboardData) return;
      event.preventDefault();
      event.clipboardData.setData('text/plain', textDerAuswahl(dokument, auswahl));
    };
    const onCut = (event: ClipboardEvent) => {
      if (gesperrtRef.current) {
        event.preventDefault();
        return;
      }
      onCopy(event);
      if (event.defaultPrevented) {
        bearbeitetRef.current = true;
        befehleRef.current.auswahlLoeschen();
      }
    };
    // Anders als Kopieren und Ausschneiden wird das Einfügen immer abgefangen, auch während einer Komposition.
    const onPaste = (event: ClipboardEvent) => {
      event.preventDefault();
      zwischenablageEinfuegen(event.clipboardData?.getData('text/plain') ?? '');
    };
    const onDrag = (event: DragEvent) => {
      event.preventDefault();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (gesperrtRef.current) {
        const kopieren = (event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'c';
        if (event.key !== 'Tab' && !kopieren) event.preventDefault();
        return;
      }
      if (event.isComposing || event.keyCode === 229 || kompositionRef.current !== undefined) return;
      // Vor jeder Taste die DOM-Auswahl übernehmen und eine Stelle in einer Insel korrigieren; sonst
      // feuert Chromium für das Zeichen kein beforeinput und die Eingabe ginge verloren.
      auswahlUebernehmen(true);
      const aktuell = zustandRef.current;
      if (event.key === 'Escape' && aktuell.bereich !== 'text') {
        event.preventDefault();
        befehleRef.current.bereichWechseln('text', aktuell.aktiveSeite);
        return;
      }
      const modifikator = event.metaKey || event.ctrlKey || event.altKey;
      if (!modifikator && (event.key === 'Backspace' || event.key === 'Delete')) {
        // An Seitengrenzen (nächste editierbare Stelle liegt hinter Kopf-/Fußinseln) feuert Chromium
        // kein beforeinput; einfaches Löschen läuft deshalb immer über das Modell.
        event.preventDefault();
        auswahlUebernehmen(false);
        bearbeitetRef.current = true;
        if (event.key === 'Backspace') befehleRef.current.rueckwaerts();
        else befehleRef.current.vorwaerts();
        return;
      }
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      const taste = event.key.toLowerCase();
      if (!['a', 'z', 'y', 'b', 'i', 'u'].includes(taste)) return;
      event.preventDefault();
      auswahlUebernehmen(false);
      const b = befehleRef.current;
      bearbeitetRef.current = true;
      if (taste === 'a') b.allesAuswaehlen();
      else if (taste === 'z') {
        if (event.shiftKey) b.wiederholen();
        else b.rueckgaengig();
      } else if (taste === 'y') b.wiederholen();
      else if (taste === 'b') b.format('fett');
      else if (taste === 'i') b.format('kursiv');
      else if (taste === 'u') b.format('unterstrichen');
    };

    host.addEventListener('beforeinput', onBeforeInput as EventListener);
    host.addEventListener('compositionstart', onCompositionStart);
    host.addEventListener('compositionend', onCompositionEnd as EventListener);
    host.addEventListener('copy', onCopy as EventListener);
    host.addEventListener('cut', onCut as EventListener);
    host.addEventListener('paste', onPaste as EventListener);
    host.addEventListener('drop', onDrag as EventListener);
    host.addEventListener('dragover', onDrag as EventListener);
    host.addEventListener('keydown', onKeyDown);
    return () => {
      host.removeEventListener('beforeinput', onBeforeInput as EventListener);
      host.removeEventListener('compositionstart', onCompositionStart);
      host.removeEventListener('compositionend', onCompositionEnd as EventListener);
      host.removeEventListener('copy', onCopy as EventListener);
      host.removeEventListener('cut', onCut as EventListener);
      host.removeEventListener('paste', onPaste as EventListener);
      host.removeEventListener('drop', onDrag as EventListener);
      host.removeEventListener('dragover', onDrag as EventListener);
      host.removeEventListener('keydown', onKeyDown);
    };
  }, [auswahlUebernehmen]);

  // Modellauswahl → DOM-Auswahl nach jedem Commit (ohne Abhängigkeitsliste, idempotent). Läuft die
  // Seitenaufteilung erst im nächsten Layout-Effekt (Messung im Elternteil), fehlt das Ziel noch; der
  // Effekt läuft dann erneut. Ohne Fokus im Host wird nichts gesetzt, außer eine Werkzeugleisten-
  // Aktion fordert den Fokus an. Eine gleichwertige DOM-Auswahl bleibt unangetastet.
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!zustand.komposition) kompositionRef.current = undefined;
    if (gesperrt) {
      // Ein spätes Kompositionsende darf Text übernehmen, aber auch beim Entsperren keinen
      // aufgeschobenen Fokuswechsel vom Speichern-Knopf zurück in den Vertrag auslösen.
      fokusBearbeitetRef.current = zustand.fokusAnforderung;
      bearbeitetRef.current = false;
      return;
    }
    if (!host || kompositionRef.current !== undefined) return;
    const { auswahl, fokusAnforderung } = zustand;
    const anfordern = fokusAnforderung !== fokusBearbeitetRef.current;
    if (anfordern) fokusBearbeitetRef.current = fokusAnforderung;
    // Während eines Maus-Ziehens gehört die DOM-Auswahl dem Browser: Das Modell folgt ihr (selectionchange),
    // die Korrektur geklemmter Stellen (Lücke, Insel) holt mouseup nach. Sonst bräche eine Auswahl über
    // die Seitengrenze mitten im Ziehen zusammen. Eine Fokusanforderung (Werkzeugleiste, Bereichswechsel
    // per Klick) wird trotzdem bedient.
    if (mausGedruecktRef.current && !anfordern) return;
    // Scrollen erst, wenn das Ziel wirklich gesetzt wurde (nach Enter/Umbruch existiert der Block
    // erst nach der Messung im nächsten Layout-Effekt).
    if (anfordern) bearbeitetRef.current = true;
    if (!auswahl) return;
    const fokusImHost = document.activeElement === host;
    if (!anfordern && !fokusImHost) return;
    if (anfordern && !fokusImHost) host.focus({ preventScroll: true });
    const rand = scrollRandOben?.() ?? SCROLL_RAND_OBEN_PX;
    const randGeaendert = letzterScrollRandRef.current !== rand;
    letzterScrollRandRef.current = rand;
    const ist = auswahlAusDom(host);
    if (ist && auswahlenGleich(ist, auswahl) && !anfordern) {
      // Auch wenn die DOM-Auswahl die Bearbeitung überstanden hat (Rücktaste, Entf im selben Textlauf), muss die
      // Schreibmarke danach sichtbar sein – unter der Werkzeugleiste und der oben klebenden Platzhalterleiste
      // (RITA-1533, gemessen 08.10.2026: Rücktaste direkt unter der Leistenkante ließ die Zeile dahinter stehen).
      if (bearbeitetRef.current || (randGeaendert && scrollRandOben && istKollabiert(auswahl))) {
        caretSichtbarMachen(document, scrollRandOben);
      }
      bearbeitetRef.current = false;
      return;
    }
    if (!auswahlInDom(host, auswahl, aktiveSeite)) return;
    if (bearbeitetRef.current) {
      caretSichtbarMachen(document, scrollRandOben);
      // Ein bestehender Folgeabsatz kann erst im Eltern-Layout-Effekt auf die neue Seite wandern.
      // Nach dessen Seitenaufteilung noch einmal pruefen, ohne Fokus oder Auswahl neu zu setzen.
      scrollenNachLayout();
    }
    bearbeitetRef.current = false;
  });

  // Klick in eine Kopf-/Fußzeile (Insel) aktiviert den Bereich; die Stelle kommt vom Klickpunkt.
  const onMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (gesperrtRef.current) {
        event.preventDefault();
        return;
      }
      const ziel = event.target as Element;
      if (ziel.closest('[data-aktion]')) {
        event.preventDefault();
        return;
      }
      if (event.button !== 0) return;
      const host = hostRef.current;
      const aktuell = zustandRef.current;
      // Klick auf einen Platzhalter oder ein Kästchen im aktiven Bereich: Chromium setzt dort keinen
      // Caret (nicht editierbar). Der Caret kommt je nach Klickstelle davor oder dahinter. Auf einem
      // Platzhalter gilt außerdem (RITA-1533, V10): Umschalt+Klick erweitert die Markierung bis vor bzw.
      // hinter ihn, Doppelklick markiert genau ihn, Dreifachklick den Absatz bzw. Aufzählungspunkt, und
      // ein Ziehen ab dem Platzhalter (auch nach Doppelklick) wird vom Editor geführt (mousemove am Dokument).
      const insel = ziel.closest('[data-knoten][contenteditable="false"]');
      if (host && insel && insel.closest('[data-bereich]')?.getAttribute('data-bereich') === aktuell.bereich) {
        const setzen = (auswahl: Auswahl) => {
          event.preventDefault();
          if (document.activeElement !== host) host.focus({ preventScroll: true });
          dispatch({ typ: 'auswahl', auswahl });
          auswahlInDom(host, auswahl, aktiveSeiteRef.current);
        };
        const grenzen = insel.hasAttribute('data-platzhalter') ? inselGrenzen(insel) : null;
        if (grenzen && kompositionRef.current === undefined) {
          if (event.shiftKey) {
            const bisher = auswahlUebernehmen(false) ?? aktuell.auswahl;
            const anker = bisher?.anker ?? grenzen.davor;
            const hinterAnker = vergleichePositionen(aktuell.dokument, anker, grenzen.davor) <= 0;
            setzen({ anker, fokus: hinterAnker ? grenzen.dahinter : grenzen.davor });
            return;
          }
          if (event.detail >= 3) {
            const container = insel.closest('li[data-eintrag]') ?? insel.closest('[data-block-id]');
            const anfang = container ? domZuPosition(container, 0) : null;
            const ende = container ? domZuPosition(container, container.children.length) : null;
            if (anfang && ende) {
              setzen({ anker: anfang, fokus: ende });
              return;
            }
          }
          ziehstartRef.current = {
            insel,
            grenzen,
            bereich: aktuell.bereich,
            x: event.clientX,
            y: event.clientY,
            begonnen: false,
          };
          if (event.detail === 2) {
            setzen({ anker: grenzen.davor, fokus: grenzen.dahinter });
            return;
          }
        }
        const rechteck = insel.getBoundingClientRect();
        const dahinter = event.clientX >= rechteck.left + rechteck.width / 2;
        const stelle = domZuPosition(insel, dahinter ? 1 : 0);
        if (stelle) {
          setzen(kollabiert(stelle));
          return;
        }
      }
      const bereichElement = ziel.closest('[data-bereich]');
      const seiteElement = ziel.closest('[data-seite]');
      if (!bereichElement || !seiteElement) return;
      const bereich = bereichElement.getAttribute('data-bereich') ?? '';
      const seite = Number(seiteElement.getAttribute('data-seite'));
      if (!BEREICHE.includes(bereich)) return;
      if (bereich === aktuell.bereich && (bereich === 'text' || seite === aktuell.aktiveSeite)) return;
      event.preventDefault();
      const stelle = positionAmPunkt(document, event.clientX, event.clientY);
      const auswahl = stelle && stelle.bereich === bereich ? kollabiert(stelle) : undefined;
      bearbeitetRef.current = true;
      befehleRef.current.bereichWechseln(bereich as Bereich, seite, auswahl);
    },
    [dispatch],
  );

  // Klicks auf Inseln: Kästchen umschalten, Seitenumbruch entfernen.
  const onClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (gesperrtRef.current) {
      event.preventDefault();
      return;
    }
    const ziel = event.target as Element;
    const b = befehleRef.current;
    const entfernen = ziel.closest('[data-aktion="seitenumbruch-entfernen"]');
    if (entfernen) {
      event.preventDefault();
      bearbeitetRef.current = true;
      b.seitenumbruchEntfernen(entfernen.getAttribute('data-umbruch-id') ?? '');
      return;
    }
    const kaestchen = ziel.closest('.vertrag-kaestchen');
    if (kaestchen) {
      const block = kaestchen.closest('[data-block-id]');
      const bereichElement = kaestchen.closest('[data-bereich]');
      const eintrag = kaestchen.closest('li[data-eintrag]');
      const bereich = bereichElement?.getAttribute('data-bereich') ?? '';
      if (block && BEREICHE.includes(bereich)) {
        b.kaestchenUmschalten(
          bereich as Bereich,
          block.getAttribute('data-block-id') ?? '',
          eintrag ? Number(eintrag.getAttribute('data-eintrag')) : 0,
          Number(kaestchen.getAttribute('data-knoten')),
        );
      }
    }
  }, []);

  return (
    <VertragSeiten
      dokument={zustand.dokument}
      seiten={seiten}
      modus={modus}
      geometrie={geometrie}
      bearbeitung={{ bereich: zustand.bereich, aktiveSeite, renderVersion: zustand.renderVersion }}
      hostRef={hostRef}
      onMouseDown={onMouseDown}
      onClick={onClick}
      gesperrt={gesperrt}
    />
  );
}

export default VertragEditor;
